<template>
    <div class="container-fluid py-4 py-md-5 pb-5 mb-5">
        <!-- Date Range Picker -->
        <div class="row" style="row-gap:12px;">
            <div class="col-12 col-md-6">
                <h4 class="mb-0 text-primary">Rooms</h4>
                <hr class="w-50" />
                <button
                    class="btn btn-sm btn-primary"
                    @click="toggleView"
                    :disabled="loadingRooms"
                >
                    {{ showAll ? 'Show Available Only' : 'Show All Rooms' }}
                </button>

            </div>
            <div class="col-12 col-md-6">
                <p class="mb-1">Select date to see room availability</p>
                <date-range-picker
                    :auto-apply="autoApply"
                    v-model="dateRange"
                    :ranges="ranges"
                    :opens="datepickerOpens"
                    :append-to-body="appendToBody"
                    :min-date="minDate"
                    class="custom-width-datepicker"
                    @update="onDateRangeChange"
                >
                    <template #input="picker">
                        <p v-if="picker.startDate && picker.endDate">
                            {{ formatDate(picker.startDate,'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate,'DD-MMM-YYYY') }}
                        </p>
                        <p v-else>
                            <i class="bi bi-calendar-event"></i> Filter by Date
                        </p>
                    </template>
                </date-range-picker>
            </div>
        </div>

    <div  class="py-3">
          <p class="text-muted">
           <span v-if="showAll">
             Showing all Rooms (Including reserved)
           </span>
           <span v-else>
           Showing available rooms only
           </span>
        </p>
    </div>
        <div v-if="selectedRooms.length > 0" class="alert alert-dark d-flex flex-wrap align-items-center justify-content-between mt-3" style="position:sticky;bottom:72px;z-index:5;gap:10px;">
            <span>
                <strong>{{ selectedRooms.length }} room{{ selectedRooms.length > 1 ? 's' : '' }} selected:</strong>
                {{ selectedRooms.map(r => r.product).join(', ') }}
            </span>
            <button class="btn btn-primary" @click="openReservationModal">
                <i class="bi bi-calendar-plus"></i> Make Reservation
            </button>
        </div>
        <!-- Loading State -->
        <div v-if="loadingRooms" class="text-center my-5">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading rooms...</span>
            </div>
            <p class="mt-2">Checking availability...</p>
        </div>


        <!-- Rooms Grid -->
        <div v-else class="row">
            <div v-if="filteredRooms.length === 0" class="col-12 text-center my-5">
                <p class="text-muted">No rooms available for selected dates</p>
            </div>
            <div
                v-for="room in filteredRooms"
                :key="room.id"
                class="col-6 col-md-3 mb-3"
            >
                <div
                    class="card text-center h-100 d-flex align-items-center justify-content-center"
                    :class="isRoomSelected(room) ? 'border-primary border' : 'border-secondary'"
                    style="cursor: pointer; height: 150px; position: relative;"
                    @click="toggleRoom(room)"
                    role="button"
                    :aria-label="`Select ${room.product} - ${room.is_available ? 'Available' : 'Unavailable'}`"
                    :tabindex="room.is_available ? 0 : -1"
                    @keypress.enter="toggleRoom(room)"
                >
                    <span
                        v-if="isRoomSelected(room)"
                        class="badge badge-primary"
                        style="position:absolute;top:8px;right:8px;"
                    ><i class="bi bi-check2"></i></span>
                    <div class="card-body d-flex flex-column align-items-center justify-content-center">
                        <h4 class="mb-2">{{ room.product }}</h4>
                        <span class="badge badge-secondary">
                            Standard rate - {{ convertCurrencyToLocal(room.ppu) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Selected rooms bar -->


        <!-- Reservation Modal -->
        <b-modal
            ref="reservationModal"
            modal-class="mr-fullscreen-sm"
            size="lg"
            hide-footer
            hide-header
            @hidden="resetReservationState"
            no-close-on-backdrop
        >
            <!-- Error Display -->
            <div v-if="hasReservationErrors" class="alert alert-danger alert-dismissible fade show" role="alert">
                <h5 class="alert-heading">
                    <i class="bi bi-exclamation-triangle-fill"></i> Reservation Error
                </h5>
                <ul class="mb-0">
                    <li v-for="(messages, field) in reservationErrors" :key="field">
                        <strong>{{ formatFieldName(field) }}:</strong>
                        <span v-for="(msg, idx) in messages" :key="idx"> {{ msg }}</span>
                    </li>
                </ul>
                <button type="button" class="close" @click="clearReservationErrors" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>

            <!-- Modal Header -->
            <div class="text-center pt-4 pb-3">
                <h5 class="py-2">Booking — {{ safeRoomName }}</h5>
                <p class="pb-2 text-muted">
                    {{ formattedCheckIn }} to {{ formattedCheckOut }}
                    <span class="badge badge-info ml-2">{{ nights }} night{{ nights !== 1 ? 's' : '' }}</span>
                </p>
            </div>

            <!-- Customer Selection -->
            <div class="form-group">
                <label class="font-weight-bold">Guest Information</label>
                <customer-search-select
                    v-model="reservation.customer_id"
                    placeholder="Search guest…"
                    :class="validationErrors.customer ? 'is-invalid' : ''"
                    @customer-selected="selectedCustomer = $event || {}"
                />
                <small v-if="validationErrors.customer" class="text-danger">
                    {{ validationErrors.customer }}
                </small>
            </div>

            <!-- Date Adjustment -->
            <div class="form-group py-3">
                <label class="font-weight-bold">Adjust Dates</label>
                <date-range-picker
                    :auto-apply="autoApply"
                    v-model="dateRange"
                    :ranges="ranges"
                    :opens="datepickerOpens"
                    :append-to-body="appendToBody"
                    :min-date="minDate"
                    class="custom-width-datepicker"
                >
                    <template #input="picker">
                        <p v-if="picker.startDate && picker.endDate" class="mb-0">
                            {{ formatDate(picker.startDate,'DD-MMM-YYYY') }} - {{ formatDate(picker.endDate,'DD-MMM-YYYY') }}
                        </p>
                        <p v-else class="mb-0">
                            <i class="bi bi-calendar-event"></i> Select Dates
                        </p>
                    </template>
                </date-range-picker>
            </div>

            <!-- Guests -->
            <div class="form-row">
                <div class="form-group col-md-6">
                    <label class="font-weight-bold">Adults *</label>
                    <input
                        type="number"
                        v-model.number="reservation.adults"
                        class="form-control"
                        :class="{'is-invalid': validationErrors.adults}"
                        min="1"
                        max="10"
                    />
                    <small v-if="validationErrors.adults" class="text-danger">
                        {{ validationErrors.adults }}
                    </small>
                </div>
                <div class="form-group col-md-6">
                    <label class="font-weight-bold">Children</label>
                    <input
                        type="number"
                        v-model.number="reservation.children"
                        class="form-control"
                        min="0"
                        max="10"
                    />
                </div>
            </div>

            <!-- Board Type Selection -->
            <div class="form-group" v-if="hasRoomVariations">
                <label class="font-weight-bold">Board Type *</label>
                <div class="d-flex flex-wrap">
                    <div
                        v-for="variation in roomVariations"
                        :key="variation.id"
                        class="form-check form-check-inline mr-3 mb-2"
                    >
                        <input
                            class="form-check-input"
                            type="radio"
                            v-model="reservation.board_type"
                            :id="'radio' + variation.id"
                            name="board_type"
                            :value="variation.board_type"
                        />
                        <label
                            class="form-check-label"
                            :for="'radio' + variation.id"
                        >
                            {{ variation.name }}
                        </label>
                    </div>
                </div>
                <small v-if="validationErrors.board_type" class="text-danger">
                    {{ validationErrors.board_type }}
                </small>
            </div>

            <!-- Discount -->
            <div class="form-group">
                <label class="font-weight-bold">Discount (Optional)</label>
                <input
                    type="number"
                    v-model.number="reservation.discount"
                    class="form-control"
                    min="0"
                    placeholder="Enter discount amount"
                />
            </div>

            <!-- Quote Display -->
            <div v-if="loadingQuote" class="text-center my-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Calculating price...</span>
                </div>
                <p class="mt-2">Calculating your quote...</p>
            </div>

            <div v-else-if="!currentQuote && hasSelectedCustomer" class="alert alert-info my-4">
                <i class="bi bi-info-circle"></i>
                <span v-if="hasRoomVariations && !reservation.board_type">
                    Please select a board type to see pricing
                </span>
                <span v-else>
                    Quote will be generated automatically
                </span>
            </div>

            <div v-else-if="!currentQuote && !hasSelectedCustomer" class="alert alert-warning my-4">
                <i class="bi bi-exclamation-triangle"></i>
                Please select a guest to see pricing
            </div>

            <div v-else-if="currentQuote" class="card shadow-sm my-4">
                <div class="card-header bg-primary text-white">
                    <h5 class="mb-0">
                        <i class="bi bi-receipt"></i> Reservation Quote
                    </h5>
                </div>
                <div class="card-body">
                    <!-- Stay Summary -->
                    <h6 class="mb-3 font-weight-bold">Stay Details</h6>
                    <div class="row mb-3">
                        <div class="col-6">
                            <small class="text-muted">Check-In</small>
                            <p class="mb-1"><strong>{{ currentQuote.check_in }}</strong></p>
                        </div>
                        <div class="col-6">
                            <small class="text-muted">Check-Out</small>
                            <p class="mb-1"><strong>{{ currentQuote.check_out }}</strong></p>
                        </div>
                        <div class="col-6">
                            <small class="text-muted">Nights</small>
                            <p class="mb-1"><strong>{{ currentQuote.nights }}</strong></p>
                        </div>
                        <div class="col-6">
                            <small class="text-muted">Guests</small>
                            <p class="mb-1">
                                <strong>{{ currentQuote.adults }} Adult(s), {{ currentQuote.children }} Child(ren)</strong>
                            </p>
                        </div>
                    </div>

                    <!-- Price Breakdown -->
                    <h6 class="mb-3 font-weight-bold">Price Breakdown</h6>
                    <div v-for="(segment, idx) in currentQuote.segments" :key="idx" class="border rounded p-3 mb-3 bg-light">
                        <div v-if="segment.room_name" class="d-flex justify-content-between mb-2">
                            <span>Room:</span>
                            <strong>{{ segment.room_name }}</strong>
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                            <span>Board Type:</span>
                            <strong>{{ segment.board_type || 'Room Only' }}</strong>
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                            <span>Rate per Night:</span>
                            <strong>{{ segment.rate_per_night }}</strong>
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                            <span>Nights:</span>
                            <strong>{{ segment.nights }}</strong>
                        </div>
                        <div class="d-flex justify-content-between mb-2">
                            <span>Subtotal:</span>
                            <strong>{{ convertCurrencyToLocal(segment.price_total) }}</strong>
                        </div>
                    </div>

                    <!-- Discount -->
                    <div v-if="currentQuote.discount > 0" class="d-flex justify-content-between mb-2 text-success">
                        <span>Discount Applied:</span>
                        <strong>- {{ convertCurrencyToLocal(currentQuote.discount) }}</strong>
                    </div>

                    <!-- Total -->
                    <div class="alert alert-secondary d-flex justify-content-between align-items-center mb-0">
                        <strong>Total Price</strong>
                        <h4 class="mb-0">{{ convertCurrencyToLocal(currentQuote.total) }}</h4>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="row mt-4">
                <div class="col-6">
                    <button
                        @click="closeReservationModal"
                        class="btn btn-outline-secondary btn-block"
                        :disabled="isProcessing"
                    >
                        Cancel
                    </button>
                </div>
                <div class="col-6">
                    <button
                        @click="proceedToPayment"
                        class="btn btn-primary btn-block"
                        :disabled="!canProceedToPayment"
                    >
                        <span v-if="isProcessing">
                            <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                            Processing...
                        </span>
                        <span v-else>
                            Proceed to Payment
                        </span>
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- Payment Modal -->
        <b-modal
            ref="paymentModal"
            modal-class="mr-fullscreen-sm"
            hide-footer
            hide-header
            no-close-on-backdrop
            @hidden="onPaymentModalHidden"
        >
            <div class="text-center py-3">
                <h3><i class="bi bi-credit-card"></i> Payment Details</h3>
                <p class="text-muted">Complete your reservation</p>
            </div>

            <div v-if="currentQuote" class="alert alert-secondary mb-4">
                <div class="d-flex justify-content-between align-items-center">
                    <span class="font-weight-bold">Total Amount Due:</span>
                    <h4 class="mb-0">{{ convertCurrencyToLocal(currentQuote.total) }}</h4>
                </div>
            </div>

            <form @submit.prevent="makeReservation">
                <div class="form-group">
                    <label class="font-weight-bold">Payment Method *</label>
                    <select
                        v-model="reservation.paymentMethod"
                        class="form-control"
                        :class="{'is-invalid': validationErrors.paymentMethod}"
                        required
                    >
                        <option :value="null" disabled>Choose Mode of Payment</option>
                        <option
                            v-for="method in paymentMethods"
                            :key="method.id"
                            :value="method.id"
                        >
                            {{ method.payment_method }}{{ method.account_label ? ' · ' + method.account_label : method.account_number ? ' · ' + method.account_number : '' }}
                        </option>
                    </select>
                    <small v-if="validationErrors.paymentMethod" class="text-danger">
                        {{ validationErrors.paymentMethod }}
                    </small>
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">Amount</label>
                    <input
                        v-if="currentQuote"
                        class="form-control"
                        :value="convertCurrencyToLocal(currentQuote.total)"
                    />
                </div>

                <div class="form-row">
                    <div class="col-6">
                        <button
                            type="button"
                            @click="closePaymentModal"
                            class="btn btn-outline-secondary btn-block"
                            :disabled="isProcessing"
                        >
                            Back
                        </button>
                    </div>
                    <div class="col-6">
                        <button
                            type="submit"
                            class="btn btn-primary btn-block"
                            :disabled="isProcessing || !reservation.paymentMethod"
                        >
                            <span v-if="isProcessing">
                                <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                                Processing...
                            </span>
                            <span v-else>
                                <i class="bi bi-check-circle"></i> Complete Payment
                            </span>
                        </button>
                    </div>
                </div>
            </form>
        </b-modal>

        <!-- Customer Selection Modal -->
        <b-modal
            ref="customersModal"
            modal-class="mr-fullscreen-sm"
            hide-footer
            @hidden="onCustomerModalHidden"
        >
            <template #modal-header>
                <h5 class="modal-title">Select Guest</h5>
                <button
                    type="button"
                    class="close"
                    @click="$refs.customersModal.hide()"
                    aria-label="Close"
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </template>

            <div class="mb-3 text-right">
                <button
                    class="btn btn-primary btn-sm"
                    @click="showNewCustomerModal"
                >
                    <i class="bi bi-plus-circle"></i> New Guest
                </button>
            </div>

            <div class="form-group has-search">
                <span class="fa fa-search form-control-feedback"></span>
                <input
                    type="text"
                    v-model="searchCustomer"
                    class="form-control"
                    placeholder="Search by name or phone"
                    @input="debouncedSearchCustomers"
                />
            </div>

            <div v-if="loadingCustomers" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading customers...</span>
                </div>
            </div>

            <ul v-else class="list-unstyled" style="max-height: 400px; overflow-y: auto;">
                <li
                    v-for="customer in filteredCustomers"
                    :key="customer.id"
                    class="border-bottom  cursor-pointer hover-bg-light"
                    @click="selectCustomer(customer)"
                    role="button"
                    tabindex="0"
                    @keypress.enter="selectCustomer(customer)"
                >
                    <div class="row">
                        <div class="col-7">
                            <h6>{{ customer.full_name }}</h6>
                            <small class="text-muted">{{ customer.address_address || 'No address' }}</small>
                        </div>
                        <div class="col-5 text-right">
                            <small class="text-muted">{{ customer.phone || 'No phone' }}</small>
                        </div>
                    </div>
                </li>
                <li v-if="filteredCustomers.length === 0" class="text-center text-muted py-4">
                    No guests found
                </li>
            </ul>
        </b-modal>

        <!-- New Customer Modal -->
        <b-modal
            ref="newCustomerModal"
            modal-class="mr-fullscreen-sm"
            hide-footer
            @hidden="onNewCustomerModalHidden"
        >
            <template #modal-header>
                <button
                    type="button"
                    class="btn btn-link p-0"
                    @click="showCustomersModal"
                >
                    <i class="fas fa-angle-left"></i> Back
                </button>
                <h5 class="modal-title ml-3">Add New Guest</h5>
            </template>

            <div v-if="customerFormError" class="alert alert-danger" role="alert">
                {{ customerFormError }}
            </div>

            <form @submit.prevent="createCustomer">
                <div class="form-row mb-3">
                    <div class="col-6">
                        <label class="font-weight-bold">First Name *</label>
                        <input
                            class="form-control"
                            v-model="customerForm.f_name"
                            type="text"
                            placeholder="First Name"
                            required
                        />
                    </div>
                    <div class="col-6">
                        <label class="font-weight-bold">Last Name *</label>
                        <input
                            class="form-control"
                            v-model="customerForm.l_name"
                            type="text"
                            placeholder="Last Name"
                            required
                        />
                    </div>
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">Email</label>
                    <input
                        class="form-control"
                        v-model="customerForm.c_email"
                        type="email"
                        placeholder="Email"
                    />
                </div>

                <div class="form-group">
                    <label class="font-weight-bold">Phone Number</label>
                    <input
                        class="form-control"
                        v-model="customerForm.phone_no"
                        type="text"
                        placeholder="Phone Number"
                    />
                </div>
                <div class="form-group">
                    <label class="font-weight-bold">KRA PIN</label>
                    <input
                        class="form-control"
                        v-model="customerForm.krapin"
                        type="text"
                        placeholder="Pin"
                    />
                </div>
                <div class="form-group">
                    <label class="font-weight-bold">Company Name</label>
                    <input
                        class="form-control"
                        v-model="customerForm.company"
                        type="text"
                        placeholder="Company"
                    />
                </div>

                <div class="form-row mb-3">
                    <div class="col-6">
                        <label class="font-weight-bold">House Number</label>
                        <input
                            class="form-control"
                            v-model="customerForm.h_no"
                            type="text"
                            placeholder="House Number"
                        />
                    </div>
                    <div class="col-6">
                        <label class="font-weight-bold">Apartment</label>
                        <input
                            class="form-control"
                            v-model="customerForm.apartment"
                            type="text"
                            placeholder="Apartment"
                        />
                    </div>
                </div>

                <div class="form-row mb-3">
                    <div class="col-6">
                        <label class="font-weight-bold">Street</label>
                        <input
                            class="form-control"
                            v-model="customerForm.street"
                            type="text"
                            placeholder="Street"
                        />
                    </div>
                    <div class="col-6">
                        <label class="font-weight-bold">Town</label>
                        <input
                            class="form-control"
                            v-model="customerForm.town"
                            type="text"
                            placeholder="Town"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    class="btn btn-primary btn-block"
                    :disabled="isProcessing || !isCustomerFormValid"
                >
                    <span v-if="isProcessing">
                        <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
                        Adding Guest...
                    </span>
                    <span v-else>
                        <i class="bi bi-person-plus"></i> Add Guest
                    </span>
                </button>
            </form>
        </b-modal>
    </div>
</template>

<script>
import axios from "axios";
import DateRangePicker from "vue2-daterange-picker/src/components/DateRangePicker";
import customerCacheService from "../IndexDB/customerCacheService";
import CustomerSearchSelect from "../partials/CustomerSearchSelect.vue";

// Debounce utility
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

export default {
    name: "ReservationPage",
    components: {
        DateRangePicker,
        CustomerSearchSelect,
    },

    data() {
        return {
            // Room data
            rooms: [],
            showAll: true,
            loadingRooms: false,

            // Date range
            dateRange: {
                startDate: moment().startOf("day"),
                endDate: moment().add(1, "days").startOf("day"),
            },
            minDate: moment().startOf("day").toDate(),
            autoApply: true,
            appendToBody: true,
            datepickerOpens: 'right',
            ranges: false,

            // Selected rooms (multi-select)
            selectedRooms: [],

            // Reservation data
            reservation: {
                customer_id: null,
                product_id: null,
                adults: 1,
                children: 0,
                paymentMethod: null,
                board_type: null,
                discount: 0,
            },

            // Quote state
            currentQuote: null,
            loadingQuote: false,
            quoteError: null,

            // Customer data
            selectedCustomer: {},
            allCustomers: [],
            searchCustomer: '',
            loadingCustomers: false,

            // Customer form
            customerForm: {
                f_name: '',
                l_name: '',
                c_email: '',
                phone_no: '',
                h_no: '',
                apartment: '',
                street: '',
                town: '',
                company: '',
                krapin: '',
            },
            customerFormError: '',

            // Payment methods
            paymentMethods: [],

            // Error handling
            reservationErrors: {},
            validationErrors: {},

            // Processing state
            isProcessing: false,

            // Watchers cleanup
            unwatchQuoteInputs: null,
        };
    },

    computed: {
        filteredRooms() {
            if (this.showAll) return this.rooms;
            return this.rooms.filter(r => r.is_available);
        },

        nights() {
            if (!this.dateRange?.startDate || !this.dateRange?.endDate) return 1;
            const days = moment(this.dateRange.endDate).diff(
                moment(this.dateRange.startDate),
                'days'
            );
            return days > 0 ? days : 1;
        },

        selectedRoom() {
            return this.selectedRooms[0] || null;
        },

        safeRoomName() {
            if (this.selectedRooms.length === 0) return 'Room';
            if (this.selectedRooms.length === 1) return this.selectedRooms[0].product;
            return this.selectedRooms.map(r => r.product).join(', ');
        },

        formattedCheckIn() {
            return this.formatDate(this.dateRange.startDate, 'DD-MMM-YYYY');
        },

        formattedCheckOut() {
            return this.formatDate(this.dateRange.endDate, 'DD-MMM-YYYY');
        },

        hasSelectedCustomer() {
            return this.selectedCustomer && this.selectedCustomer.id;
        },

        hasRoomVariations() {
            return this.selectedRoom?.variations?.length > 0;
        },

        roomVariations() {
            return this.selectedRoom?.variations || [];
        },

        isMultiRoom() {
            return this.selectedRooms.length > 1;
        },

        hasReservationErrors() {
            return Object.keys(this.reservationErrors).length > 0;
        },

        canProceedToPayment() {
            return (
                !this.isProcessing &&
                !this.loadingQuote &&
                this.currentQuote &&
                this.hasSelectedCustomer &&
                this.reservation.adults >= 1 &&
                (!this.hasRoomVariations || this.reservation.board_type)
            );
        },

        filteredCustomers() {
            if (!Array.isArray(this.allCustomers)) return [];
            if (!this.searchCustomer) return this.allCustomers;

            const search = this.searchCustomer.toLowerCase();
            return this.allCustomers.filter(customer => {
                if (!customer) return false;
                const name = (customer.full_name || '').toLowerCase();
                const phone = (customer.phone || '').toLowerCase();
                return name.includes(search) || phone.includes(search);
            });
        },

        isCustomerFormValid() {
            return (
                this.customerForm.f_name.trim() &&
                this.customerForm.l_name.trim() &&
                (this.customerForm.c_email.trim() || this.customerForm.phone_no.trim())
            );
        },
    },

    watch: {
        dateRange: {
            handler() {
                this.onDateRangeChange();
            },
            deep: true
        },

        'reservation.adults'() {
            this.debouncedRefreshQuote();
        },

        'reservation.children'() {
            this.debouncedRefreshQuote();
        },

        'reservation.board_type'() {
            this.debouncedRefreshQuote();
        },

        'reservation.discount'() {
            this.debouncedRefreshQuote();
        },

        'reservation.customer_id'() {
            // Immediate quote generation when customer is selected (no debounce)
            if (this.reservation.customer_id && this.selectedRoom) {
                this.getQuote();
            }
        },

        selectedRooms: {
            handler() {
                if (this.selectedRooms.length > 0 && this.hasSelectedCustomer) {
                    this.getQuote();
                }
            },
            deep: true,
        },
    },

    methods: {
        // === Room Management ===
        async fetchRooms() {
            if (!this.dateRange?.startDate || !this.dateRange?.endDate) return;

            this.loadingRooms = true;
            try {
                const res = await axios.get(`/api/reservations/availability`, {
                    params: {
                        check_in: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
                        check_out: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
                        all_rooms: this.showAll ? 1 : 0
                    },
                });

                // Transform response - convert object to array and add is_available flag
                const roomsData = res.data || {};
                this.rooms = Object.values(roomsData).map(room => ({
                    ...room,
                    // Assume all returned rooms are available unless API specifies otherwise
                    is_available: room.is_available !== undefined ? room.is_available : true
                }));
            } catch (err) {
                console.error('Failed to fetch rooms:', err);
                this.$toasts.push({
                    type: 'error',
                    message: 'Failed to load room availability'
                });
                this.rooms = [];
            } finally {
                this.loadingRooms = false;
            }
        },

        async toggleView() {
            this.showAll = !this.showAll;
            await this.fetchRooms();
        },

        onDateRangeChange() {
            // Fetch new room availability
            this.fetchRooms();
            // Invalidate current quote and regenerate after debounce
            this.invalidateQuote();
            this.debouncedRefreshQuote();
        },

        // === Reservation Flow ===
        isRoomSelected(room) {
            return this.selectedRooms.some(r => r.id === room.id);
        },

        toggleRoom(room) {
            if (!this.showAll && room.is_available === false) {
                this.$toasts.push({ type: 'warning', message: `${room.product} is not available for selected dates` });
                return;
            }
            const idx = this.selectedRooms.findIndex(r => r.id === room.id);
            if (idx >= 0) {
                this.selectedRooms.splice(idx, 1);
            } else {
                this.selectedRooms.push(room);
            }
        },

        openReservationModal() {
            if (this.selectedRooms.length === 0) return;

            // Auto-select board type from primary room if only one option
            const primary = this.selectedRooms[0];
            if (primary.variations?.length === 1) {
                this.reservation.board_type = primary.variations[0].board_type;
            }

            this.reservation.product_id = primary.id;
            this.clearReservationErrors();
            this.$refs.reservationModal.show();

            if (this.hasSelectedCustomer) {
                this.getQuote();
            }
        },

        closeReservationModal() {
            this.$refs.reservationModal.hide();
        },

        resetReservationState() {
            this.selectedRooms = [];
            this.currentQuote = null;
            this.quoteError = null;
            this.clearReservationErrors();
            this.clearValidationErrors();

            // Reset reservation form but keep customer
            this.reservation = {
                ...this.reservation,
                product_id: null,
                adults: 1,
                children: 0,
                board_type: null,
                discount: 0,
                paymentMethod: null,
            };
        },

        // === Quote Management ===
        async getQuote() {
            if (!this.validateReservationInputs()) return;
            if (this.selectedRooms.length === 0) return;

            this.loadingQuote = true;
            this.quoteError = null;

            const baseParams = {
                customer_id: this.reservation.customer_id,
                check_in:  moment(this.dateRange.startDate).format('YYYY-MM-DD'),
                check_out: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
                adults:    this.reservation.adults,
                children:  this.reservation.children,
                board_type: this.reservation.board_type,
                discount:  this.reservation.discount,
            };

            try {
                const responses = await Promise.all(
                    this.selectedRooms.map(room =>
                        axios.post('/api/reservations/quote', { ...baseParams, product_id: room.id })
                    )
                );
                const quotes = responses.map(r => r.data);

                if (quotes.length === 1) {
                    this.currentQuote = quotes[0];
                } else {
                    // Aggregate multi-room quotes
                    this.currentQuote = {
                        ...quotes[0],
                        segments: quotes.flatMap((q, i) =>
                            q.segments.map(s => ({ ...s, room_name: this.selectedRooms[i].product }))
                        ),
                        discount: quotes.reduce((sum, q) => sum + (q.discount || 0), 0),
                        total:    quotes.reduce((sum, q) => sum + q.total, 0),
                    };
                }
                this.clearReservationErrors();
            } catch (err) {
                this.quoteError = 'Failed to generate quote';
                this.currentQuote = null;
                if (err.response?.data?.errors) {
                    this.reservationErrors = err.response.data.errors;
                }
                console.error('Quote generation failed:', err);
            } finally {
                this.loadingQuote = false;
            }
        },

       validateAndRefreshQuote() {
            if (!this.hasSelectedCustomer) return;
            if (!this.selectedRoom) return;
            if (this.reservation.adults < 1) return;

            if (this.hasRoomVariations && !this.reservation.board_type) {
                return;
            }

            this.getQuote();
        },

        // // Separate debounced method for quote refresh
        // debouncedRefreshQuote: debounce(function() {
        //     this.validateAndRefreshQuote();
        // }, 800),

        invalidateQuote() {
            this.currentQuote = null;
            this.quoteError = null;
        },

        // === Payment Flow ===
        async proceedToPayment() {
            if (!this.canProceedToPayment) return;

            this.clearValidationErrors();

            // Final validation
            if (!this.validateReservationInputs()) {
                this.$toasts.push({
                    type: 'error',
                    message: 'Please complete all required fields'
                });
                return;
            }

            // Ensure fresh quote
            if (!this.currentQuote) {
                await this.getQuote();
            }

            if (this.currentQuote) {
                await this.fetchPaymentMethods();
                this.$refs.paymentModal.show();
            }
        },

        async fetchPaymentMethods() {
            try {
                const res = await axios.get('/api/payments');
                this.paymentMethods = res.data || [];
            } catch (err) {
                console.error('Failed to fetch payment methods:', err);
                this.paymentMethods = [];
            }
        },

        async makeReservation() {
            if (!this.reservation.paymentMethod) {
                this.validationErrors.paymentMethod = 'Please select a payment method';
                return;
            }

            if (!this.currentQuote) {
                this.$toasts.push({
                    type: 'error',
                    message: 'Quote expired. Please regenerate.'
                });
                return;
            }

            this.isProcessing = true;
            this.clearReservationErrors();



            try {
                await axios.post("/api/reservations", {
                    ...this.reservation,
                    product_ids: this.selectedRooms.map(r => r.id),
                    check_in:  moment(this.dateRange.startDate).format('YYYY-MM-DD'),
                    check_out: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
                    total_amount: this.currentQuote.total,
                    discount: this.currentQuote.discount,
                    status: 'confirmed',
                    payment_method: this.reservation.paymentMethod,
                });

                this.$toasts.push({
                    type: 'success',
                    message: 'Reservation created successfully!'
                });

                // Give user time to see success message
                setTimeout(() => {
                    window.location.href = '/reservations';
                }, 2000);

            } catch (err) {
                this.isProcessing = false;

                if (err.response?.data?.errors) {
                    this.reservationErrors = err.response.data.errors;
                    this.$refs.paymentModal.hide();
                } else {
                    this.$toasts.push({
                        type: 'error',
                        message: 'Failed to create reservation. Please try again.'
                    });
                }

                console.error('Reservation failed:', err);
            }
        },

        closePaymentModal() {
            this.$refs.paymentModal.hide();
        },

        onPaymentModalHidden() {
            if (!this.isProcessing) {
                this.reservation.paymentMethod = null;
            }
        },

        // === Customer Management ===
        async getCustomers() {
            this.loadingCustomers = true;
            try {
                const customers = await customerCacheService.getCustomers(
                    this.searchCustomer,
                    true
                );
                this.allCustomers = Array.isArray(customers) ? customers : [];
            } catch (err) {
                console.error('Failed to fetch customers:', err);
                this.allCustomers = [];
            } finally {
                this.loadingCustomers = false;
            }
        },

        debouncedSearchCustomers: debounce(function() {
            this.getCustomers();
        }, 300),

        showCustomersModal() {
            this.getCustomers();
            this.$refs.newCustomerModal?.hide();
            this.$refs.customersModal.show();
        },

        showNewCustomerModal() {
            this.resetCustomerForm();
            this.$refs.customersModal.hide();
            this.$refs.newCustomerModal.show();
        },

        selectCustomer(customer) {
            this.selectedCustomer = customer;
            this.reservation.customer_id = customer.id;
            this.$refs.customersModal.hide();
            this.clearValidationErrors();

            if (this.selectedRooms.length > 0) {
                this.getQuote();
            }
        },

        async createCustomer() {
            if (!this.isCustomerFormValid) {
                this.customerFormError = 'Please enter at least first name, last name, and email or phone';
                return;
            }

            this.isProcessing = true;
            this.customerFormError = '';

            try {
                const res = await axios.post('/api/newcustomer', {
                    ...this.customerForm
                });

                if (res.status === 200 && res.data.customer) {
                    this.selectedCustomer = res.data.customer;
                    this.reservation.customer_id = this.selectedCustomer.id;

                    this.$toasts.push({
                        type: 'success',
                        message: 'Guest added successfully'
                    });

                    this.$refs.newCustomerModal.hide();

                    if (this.selectedRooms.length > 0) {
                        this.getQuote();
                    }
                }
            } catch (err) {
                this.customerFormError = err.response?.data?.message ||
                    'Failed to add guest. Please try again.';
                console.error('Failed to create customer:', err);
            } finally {
                this.isProcessing = false;
            }
        },

        resetCustomerForm() {
            this.customerForm = {
                f_name: '',
                l_name: '',
                c_email: '',
                phone_no: '',
                h_no: '',
                apartment: '',
                street: '',
                town: '',
            };
            this.customerFormError = '';
        },

        onCustomerModalHidden() {
            this.searchCustomer = '';
        },

        onNewCustomerModalHidden() {
            this.resetCustomerForm();
        },

        // === Validation ===
        validateReservationInputs() {
            this.clearValidationErrors();
            let isValid = true;

            if (!this.hasSelectedCustomer) {
                this.validationErrors.customer = 'Please select a guest';
                isValid = false;
            }

            if (!this.reservation.adults || this.reservation.adults < 1) {
                this.validationErrors.adults = 'At least 1 adult required';
                isValid = false;
            }

            if (this.hasRoomVariations && !this.reservation.board_type) {
                this.validationErrors.board_type = 'Please select a board type';
                isValid = false;
            }

            return isValid;
        },

        clearValidationErrors() {
            this.validationErrors = {};
        },

        clearReservationErrors() {
            this.reservationErrors = {};
        },

        // === Utility Methods ===
        formatDate(date, format) {
            if (!date) return '';
            return moment(date).format(format);
        },

        formatFieldName(field) {
            return field
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase());
        },

        convertCurrencyToLocal(amount) {
            if (typeof this.$root.convertCurrencyToLocal === 'function') {
                return this.$root.convertCurrencyToLocal(amount);
            }
            return `${parseFloat(amount || 0).toFixed(2)}`;
        },
    },

    mounted() {
        this.fetchRooms();

        this.debouncedRefreshQuote = debounce(
            () => {
                this.validateAndRefreshQuote();
            },
            500
        );
    },

    beforeDestroy() {
        // Cleanup
        if (this.unwatchQuoteInputs) {
            this.unwatchQuoteInputs();
        }
          if (this.debouncedRefreshQuote?.cancel) {
            this.debouncedRefreshQuote.cancel();
        }
    },
};
</script>

<style>
/* b-modal content is teleported to <body>, outside scoped styles. */
@media (max-width: 575.98px) {
  .mr-fullscreen-sm .modal-dialog { margin: 0; max-width: 100%; min-height: 100%; }
  .mr-fullscreen-sm .modal-content { border: 0; border-radius: 0; min-height: 100vh; }
}
</style>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}

.hover-bg-light:hover {
    background-color: #f8f9fa;
}

.opacity-50 {
    opacity: 0.5;
}

.has-search .form-control-feedback {
    position: absolute;
    z-index: 2;
    display: block;
    width: 2.375rem;
    height: 2.375rem;
    line-height: 2.375rem;
    text-align: center;
    pointer-events: none;
    color: #aaa;
}

.has-search .form-control {
    padding-left: 2.375rem;
}

.custom-width-datepicker {
    width: 100%;
}
</style>
